export const SIDEBAR_SECTION_STORAGE_KEY = 'sidebar-expanded-sections';
export const APPLICATION_SIDEBAR_SECTION_STORAGE_KEY =
  'application-sidebar-expanded-sections';

export const SIDEBAR_SECTION_IDS = [
  'platform',
  'group',
  'application',
] as const;

export type SidebarSectionId = (typeof SIDEBAR_SECTION_IDS)[number];

export const DEFAULT_EXPANDED_SIDEBAR_SECTIONS: SidebarSectionId[] = [
  'platform',
  'group',
  'application',
];

export const APPLICATION_SIDEBAR_SECTION_IDS = [
  'accounts-and-utilities',
  'operations',
  'finance',
  'insights',
] as const;

export type ApplicationSidebarSectionId =
  (typeof APPLICATION_SIDEBAR_SECTION_IDS)[number];

export const DEFAULT_EXPANDED_APPLICATION_SIDEBAR_SECTIONS: ApplicationSidebarSectionId[] =
  [...APPLICATION_SIDEBAR_SECTION_IDS];

const createSidebarSectionStorageHelpers = <TSectionId extends string>(
  sectionIds: readonly TSectionId[],
  defaultExpandedSectionIds: TSectionId[]
) => {
  const sectionIdSet = new Set<string>(sectionIds);

  const getInitialExpandedSections = (savedValue: string | null) => {
    if (!savedValue) {
      return defaultExpandedSectionIds;
    }

    try {
      const parsed = JSON.parse(savedValue);

      if (!Array.isArray(parsed)) {
        return defaultExpandedSectionIds;
      }

      return parsed.filter((sectionId): sectionId is TSectionId => {
        return typeof sectionId === 'string' && sectionIdSet.has(sectionId);
      });
    } catch {
      return defaultExpandedSectionIds;
    }
  };

  const toggleExpandedSection = (
    expandedSectionIds: TSectionId[],
    sectionId: TSectionId
  ) => {
    if (expandedSectionIds.includes(sectionId)) {
      return expandedSectionIds.filter(
        (expandedSectionId) => expandedSectionId !== sectionId
      );
    }

    return [...expandedSectionIds, sectionId];
  };

  return {
    getInitialExpandedSections,
    toggleExpandedSection,
  };
};

const sidebarSectionStorageHelpers = createSidebarSectionStorageHelpers(
  SIDEBAR_SECTION_IDS,
  DEFAULT_EXPANDED_SIDEBAR_SECTIONS
);
const applicationSidebarSectionStorageHelpers =
  createSidebarSectionStorageHelpers(
    APPLICATION_SIDEBAR_SECTION_IDS,
    DEFAULT_EXPANDED_APPLICATION_SIDEBAR_SECTIONS
  );

export const getInitialExpandedSidebarSections =
  sidebarSectionStorageHelpers.getInitialExpandedSections;
export const toggleExpandedSidebarSection =
  sidebarSectionStorageHelpers.toggleExpandedSection;

export const getInitialExpandedApplicationSidebarSections =
  applicationSidebarSectionStorageHelpers.getInitialExpandedSections;
export const toggleExpandedApplicationSidebarSection =
  applicationSidebarSectionStorageHelpers.toggleExpandedSection;
